<script lang="ts">
	/**
	 * This component includes the actual analysis/analytic, its values and recommendations.
	 * It receives the data from AnalyticalPage, which manages the reception of the data from the server and creates
	 * necessary analytics.
	 * The actual presentation of this data as well as saving that data is done here.
	 */

	import { t } from '$i18n/i18n.ts'
	import {
		analyticalMetadata,
		analyticNames,
		getFormattedParameters,
		hideOptionalValues,
		loadRecommendations,
		parameterStore,
		values
	} from './stores'
	import { addRenderer } from 'svelecte'
	import {
		AnalyticalParentType,
		AnalyticalType,
		ButtonIcons,
		DangerKind,
		InputIcons
	} from '../types/enums'
	import { onDestroy, onMount, setContext } from 'svelte'
	import InputField from '../molecules/InputField.svelte'
	import Switch from '../atoms/Switch.svelte'
	import { inputFieldFormats } from '../utils/formatters'
	import Button from '#lib/atoms/Button.svelte'
	import EvaluationCard from './EvaluationCard.svelte'
	import AnalysisTable from './AnalysisTable.svelte'
	import Recommendations from './Recommendations.svelte'
	import ArbitraryValuesForm from './ArbitraryValuesForm.svelte'
	import TransferDeclarationParametersDialog from './TransferDeclarationParametersDialog.svelte'
	import { debounce } from '$utils/utils.svelte'
	import AutocompleteInputField from '../autocomplete/AutocompleteInputField.svelte'
	import { AutocompleteConfig } from '../types/autocompleteFieldConfig'
	import { DefaultRobotoFontProps } from '../types/fontProps'
	import BasicWithSidebar from '../templates/BasicWithSidebar.svelte'
	import { type Unsubscriber, writable } from 'svelte/store'
	import type { AnalyticDto } from './types/analyticDto'
	import type {
		AnalyticalEvaluation,
		DangerAssessment,
		EvaluationResult,
		IgnoredValues,
		ValueResults
	} from './types/analyticalEvaluation'
	import { equivalenceMark, getValueLabel } from './utils/equivalence'
	import type { EvaluationParameter } from './types/analyticalResponse'
	import type { DeclarationParameterEntry } from './types/declarationParameter'
	import {
		evaluateAnalysis,
		fetchDeclarationParameterPreview,
		handleCatch,
		persistAnalysis
	} from './AnalyticsTool.controller'
	import ArrowLeft from '$svg/icon-arrow-left.svg?component'
	import Checkmark from '$svg/icon-checkmark.svg?component'

	setContext('useMaterialDesign', true)
	setContext('stickyHeightInformation', writable('30px'))

	export let editable = false
	export let title: string = '-'
	export let analytic: AnalyticDto
	export let description: string = ''
	export let labDescription: string = ''
	export let expectedSatisfyingResult: string | undefined
	export let parameters: EvaluationParameter[] = []
	export let onReload: () => void = () => {}
	export let onChangeProcedure: () => void = () => {}

	let unsubscribeParameters: Unsubscriber
	let transferDialog: TransferDeclarationParametersDialog
	let pendingSave: {
		url: string
		payload: Record<string, unknown>
		lotId: string
	} | null = null

	/**
	 * by default, we show no save button, we only show a save button when we have either a new or concrete
	 * analysis to save
	 */
	let enableSave: boolean = false
	let isAnalysis: boolean // isAnalysis -> true, if analysis; false, if analytic
	let searchTerm: string = ''

	let evaluationResult: Record<string, EvaluationResult>
	let valueMetadata: AnalyticalEvaluation['valueMetadata']
	let valueResults: ValueResults
	let valueFootnotes: AnalyticalEvaluation['valueFootnotes']
	let danger: DangerAssessment | undefined
	let ignoredValues: IgnoredValues = {}
	let combinedMaxOrdinals: Record<string, number> = {}
	let { parentType, analyticalId, analyticalType, parentId } = $analyticalMetadata // get all the analytical metadata from the store

	onMount(async () => {
		unsubscribeParameters = parameterStore.subscribe(() => updateEvaluation())
	})

	onDestroy(() => {
		unsubscribeParameters()
	})

	const deactivateOptionalHidingAndReload = async () => {
		$hideOptionalValues = false
		onReload()
	}

	const updateEvaluation = debounce(() => {
		if (!analytic) {
			return
		}
		// store values in the session storage only for analysis check
		if (parentType === AnalyticalParentType.NONE || parentId === '-1') {
			window.sessionStorage.setItem('analyticalValues', JSON.stringify($values))
		}
		let parameters = [...getFormattedParameters()]
		evaluateAnalysis(analytic.technicalName, parameters, $values).then(
			(data: AnalyticalEvaluation) => {
				evaluationResult = data.combinedResult
				$analyticNames = data.analyticNames
				valueMetadata = data.valueMetadata
				valueResults = data.valueResults
				valueFootnotes = data.valueFootnotes
				analytic.optionalValues = data.optionalValues
				danger = data.danger
				// only this endpoint serves it, and it stays absent whenever nothing is ignored
				ignoredValues = data.ignoredValues ?? {}
				combinedMaxOrdinals = data.combinedMaxOrdinals
			}
		)
		loadRecommendations()
	})

	/**
	 * User clicks Speichern on the analysis page. For every lot analysis the transfer dialog opens first — the
	 * actual save (and optional material-property write) is only performed after the user clicks Speichern in the
	 * dialog. When no "Deklarationsbestimmende Parameter" field matches, the dialog still opens but explains that
	 * there is nothing to transfer, so the user always understands why it did or did not offer a transfer.
	 * Non-lot / brand-new analyses have no lot to write to and are saved directly.
	 */
	const saveValues = () => {
		if (!enableSave) {
			return
		}

		const parameters = [...getFormattedParameters()]
		const url = `/api/${analyticalType?.toLowerCase()}/${analyticalId}/update`
		const payload = {
			description,
			labDescription,
			parameters,
			values: $values,
			parentType
		}

		// non-lot analysis (or brand-new one): no dialog flow, save directly
		if (parentType !== AnalyticalParentType.LOT || !analyticalId || analyticalId === '-1') {
			persistAnalysis(url, payload, null, null)

			return
		}

		if (analyticalType === AnalyticalType.ANALYSIS) {
			// not desired for analytics
			// preview using the in-memory (unsaved) values so the dialog reflects what the user is about to commit
			fetchDeclarationParameterPreview(analyticalId, payload)
				.then((preview) => {
					pendingSave = { url, payload, lotId: preview?.lotId ?? parentId ?? '' }
					transferDialog.open(preview?.entries ?? [])
				})
				.catch(handleCatch)
		} else if (analyticalType === AnalyticalType.ANALYTIC) {
			persistAnalysis(url, payload, null, null)
		}
	}

	/**
	 * Callback from the transfer dialog when the user clicks its Speichern button. Only now is the analysis
	 * actually persisted; when the checkbox is on and a matching field exists, the (possibly edited) entries are
	 * appended to the material property as well. When the checkbox is off, any edits are discarded — only the
	 * analysis is saved.
	 */
	const applyDeclarationParameterTransfer = (
		shouldApply: boolean,
		entries: DeclarationParameterEntry[]
	) => {
		if (!pendingSave) {
			return
		}

		const { url, payload, lotId } = pendingSave
		pendingSave = null
		persistAnalysis(url, payload, shouldApply ? entries : null, lotId)
	}

	/**
	 * Redirects the user back to the previous page or to the '/projects' page if no origin is specified.
	 */
	const redirectBack = () => {
		// in case we come from another page, e.g the FAK, we have a referrer/origin specified
		const urlParams = new URLSearchParams(window.parent.location.search)
		if (urlParams.has('origin')) {
			// we should always give an origin, even when creating an analysis or analytic
			window.parent.location.href = decodeURIComponent(urlParams.get('origin') as string)
		} else {
			window.parent.location.href = `/projects`
		}
	}

	const addParameterRenderer = (parameter: string) => {
		function renderer(item: { text: string }) {
			// noinspection TypeScriptValidateTypes
			return $t(`Analytics.evaluationParameter.${parameter}.${item.text}`)
		}

		addRenderer(parameter + '-render', renderer)
		return renderer
	}

	/** a value can meet both criteria, so its chip carries every reason that applies */
	function dangerKindsOf(assessment: DangerAssessment | undefined, valueKey: string) {
		const kinds: DangerKind[] = []

		if (assessment?.dangerRangeValues?.includes(valueKey)) kinds.push(DangerKind.RANGE)
		if (assessment?.ecotoxicValues?.includes(valueKey)) kinds.push(DangerKind.ECOTOXIC)

		return kinds
	}

	$: if (valueResults) {
		// the analytic columns of this page: one for an analysis, several for a comparison
		const analyticTechnicalNames = Object.keys(evaluationResult ?? {})

		for (let i in $values) {
			let value = $values[i]
			value.evaluation = valueResults[value.key]
			value.footnote = valueFootnotes[value.key]
			value.danger = dangerKindsOf(danger, value.key)
			// the single result in value.evaluation is untouched by this, it keeps showing the raw result
			value.equivalence = equivalenceMark(
				ignoredValues,
				analyticTechnicalNames,
				value.key,
				(key) => getValueLabel(valueMetadata, key),
				(technicalName) => $analyticNames[technicalName] ?? technicalName
			)
		}
		$values = $values
	}
	// whenever the store changes, update the variables
	$: ({ parentType, analyticalId, analyticalType, parentId } = $analyticalMetadata)
	// saveEnabled, if parent data valid and not empty OR no invalid analytical id — AND must be editable
	$: enableSave =
		!!(
			(parentId && parentId !== '-1' && parentType && parentType !== AnalyticalParentType.NONE) ||
			(analyticalId && analyticalId !== '-1')
		) && editable
	$: isAnalysis = analyticalType === AnalyticalType.ANALYSIS
</script>

<BasicWithSidebar sidebarOpened={isAnalysis} contentMaxWidth="100%">
	<div class="analytic-container" slot="main-content">
		<div class="analytic-col">
			<h1 id="analytic-header" style="padding-bottom: 0.37rem;">{title}</h1>
			<h2>
				{$t(isAnalysis ? 'Analytics.evaluation' : 'Analytics.testMethod')}: {analytic?.name ?? '-'}
			</h2>
			<!-- input field to give a name to the analysis -->
			{#if isAnalysis && parentType !== AnalyticalParentType.NONE && analyticalId && analyticalId !== '-1'}
				<InputField
					bind:value={description}
					readOnly={!editable}
					format={inputFieldFormats.FULL}
					fontProps={DefaultRobotoFontProps('0.875rem', '0.75rem')}
					label={$t('Analytics.description')}
				/>
				<InputField
					bind:value={labDescription}
					readOnly={!editable}
					format={inputFieldFormats.FULL}
					fontProps={DefaultRobotoFontProps('0.875rem', '0.75rem')}
					label={$t('Analytics.labDescription')}
				/>
			{/if}

			{#if analytic}
				<!-- for analysis, we make the parameters selectable, therefore we show autocomplete fields -->
				{#if isAnalysis}
					{#each Object.entries(analytic?.requiredParameters) as [parameter, parameterValues] (parameter)}
						<!-- had to do it this way, as we are in an each loop -->
						<AutocompleteInputField
							bind:value={$parameterStore.parameters[parameter]}
							label={$t(`Analytics.evaluationParameter.${parameter}`)}
							autocompleteConfig={AutocompleteConfig(null, null, false, !editable)}
							options={parameterValues}
							containerWidth={inputFieldFormats.SEARCH.maxWidth}
							renderer={addParameterRenderer(parameter)}
							onChange={loadRecommendations}
						/>
					{/each}
				{:else}
					<!-- for analytics, we only show the selected expected result and the selected parameters as text -->
					{#if expectedSatisfyingResult && expectedSatisfyingResult.toLowerCase() !== 'eingehalten'}
						<div>
							<b>{$t('Analytics.desiredResult')}:</b><br />
							{expectedSatisfyingResult}
						</div>
					{/if}
					{#each parameters.filter((param) => param.value) as param (param.name)}
						<div>
							<b>{$t(`Analytics.evaluationParameter.${param.name}`)}:</b><br />
							{$t(`Analytics.evaluationParameter.${param.name}.${param.value}`) ?? '-'}
						</div>
					{/each}
				{/if}
			{/if}
		</div>

		<!-- top row including switch to hide optional values, change the procedure for analysis check and search for
        values in the list of values -->
		<div class="analytic-col">
			<h2>{$t('UI.filters')}</h2>
			<div class="analytic-row">
				<div class="filter-box-optional">
					<span>{$t('Analytics.switch.hideOptional')}</span>
					<Switch bind:checked={$hideOptionalValues} />
				</div>
				<div>
					<Button
						variant="primary"
						titleAttr={$t('UI.analytic.changeProcedure.btn.titleAttr')}
						onclick={onChangeProcedure}
					>
						{$t('UI.analytic.changeProcedure.btn')}
					</Button>

					<InputField
						bind:value={searchTerm}
						placeholder={$t('UI.placeholder.search')}
						materialWidth={inputFieldFormats.SEARCH.maxWidth}
						icon={InputIcons.SEARCH_LENS}
						containerWidth="auto"
					/>
				</div>
			</div>
		</div>

		{#if isAnalysis}
			<div class="sticky-top-evaluation">
				{#key evaluationResult}
					<EvaluationCard {evaluationResult} {valueMetadata} {danger} {combinedMaxOrdinals} />
				{/key}
			</div>
		{/if}

		<div class:noAnalysis={!isAnalysis}>
			<AnalysisTable
				{analytic}
				{searchTerm}
				{editable}
				on:valuesRemoved={onReload}
				on:updateValue={updateEvaluation}
			/>
		</div>

		<!-- bottom row including back button and save button, if save enabled -->
		<div class="analytic-row">
			{#if parentType !== AnalyticalParentType.CHECK}
				<Button variant="secondary" onclick={redirectBack}>
					<ArrowLeft width="24" height="24" fill="inherit" />
					{$t('UI.button.back')}
				</Button>
			{/if}
			{#if enableSave}
				<Button
					variant="primary"
					id="button_save_analysis"
					disabled={!editable}
					leftIcon={ButtonIcons.CHECKMARK_BOLD}
					onclick={saveValues}
				>
					<Checkmark stroke-width="2" width="24" height="24" fill="inherit" />
					{$t('UI.button.save')}
				</Button>
			{/if}
		</div>
	</div>

	<div slot="sidebar-content" class="analytical-sidebar-content">
		{#if isAnalysis}
			<Recommendations on:valuesChanged={deactivateOptionalHidingAndReload} {editable} />
		{/if}
		<!-- only show the values if we are not in the tool -->
		{#if editable && parentType !== AnalyticalParentType.NONE && analyticalId && analyticalId !== '-1'}
			<ArbitraryValuesForm on:valuesChanged={deactivateOptionalHidingAndReload} />
		{/if}
	</div>
</BasicWithSidebar>

<TransferDeclarationParametersDialog
	bind:this={transferDialog}
	onConfirm={applyDeclarationParameterTransfer}
/>

<style lang="scss">
	@import '$styles/global';

	.analytic-container {
		display: flex;
		flex-direction: column;
		align-items: normal;
		justify-content: normal;
		row-gap: 30px;

		padding-bottom: 2rem;
		width: 100%;
	}

	.analytic-col {
		display: flex;
		flex-direction: column;
		align-items: normal;
		justify-content: normal;
		row-gap: 14px;
	}

	.analytic-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;

		flex-wrap: wrap;
		row-gap: 0.875rem;
	}

	.filter-box-optional {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;

		font-family: Roboto, sans-serif;
		font-size: 0.75rem;
		font-weight: 400;
		line-height: normal;
		font-style: normal;

		width: 15.9375rem;
		height: 2rem;
		border: 1px solid #d9d9d9;
		border-radius: 0.125rem;
		padding-left: 0.75rem;
		padding-right: 0.87rem;

		& ~ div {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			column-gap: 0.875rem;
		}
	}

	.sticky-top-evaluation {
		position: sticky;

		/* height of header */
		top: 3.813rem;

		/* add padding to hide content "behind" card */
		padding-top: 1.88rem;
		padding-bottom: 1.88rem;

		/* negate the padding with this margin so we don't double the row-gap value */
		margin-top: -1.88rem;
		margin-bottom: -1.88rem;
		background-color: #fff;

		/* above input fields */
		z-index: 1;
	}

	div.noAnalysis {
		& :global(tr.analysis-table-header) {
			--sticky-header-top: 10px; // top space for sticky
			--sticky-header-top-padding: 61px; // height of menubar
		}
	}

	.analytical-sidebar-content {
		display: flex;
		flex-direction: column;
		align-items: normal;
		justify-content: normal;
		row-gap: 30px;

		padding-bottom: 2rem;
	}
</style>
